import { Icon } from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { mdiMenu } from "@mdi/js";
import { Popconfirm, Tabs, Tooltip } from "antd";
import { SAVE_TYPE_WELL_ERROR_MESSAGE } from "constants/errorMessages.constants";
import { ArpsDesignerKind, FilteredFolders } from "constants/forecasts.constants";
import { TYPE_WELLS } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import {
  selectExpandedForecastKeys,
  selectExpandedTypeWellKeys,
  selectHasUnsavedChanges,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";
import styled from "styled-components";

import EditTypeWellData from "components/arps/EditTypeWellData";
import { TypeWellEditorHeader } from "components/arps/widget/TypeWellEditor/TypeWellEditorHeader";
import TypeWellEditorChangeRecords from "components/arps/widget/TypeWellEditor/tabs/TypeWellEditorChangeRecords";
import TypeWellEditorWellList from "components/arps/widget/TypeWellEditor/tabs/TypeWellEditorWellList";
import ProjectForecasts from "components/project/forecasts/ProjectForecasts";
import { ForecastProvider } from "components/project/forecasts/context";
import { DEFAULT_DECLINE_TYPE_SETTING } from "components/user-settings/constants/userSetting.constants";
import { useUserSettings } from "components/user/hooks";

import {
  useArpsUndoManager,
  useTypeWellResetter,
  useTypeWellSaver,
  useTypeWellUpdater,
  useUndoRedo,
  useValNavTypeWellUpdater
} from "../../hooks";
import TypeWellCommentField from "./TypeWellCommentField";
import { ForecastArrayContent } from "./tabs/ForecastArrayContent";
import TypeWellInputTable from "./tabs/TypeWellInputTable";

const ARPS_EDITOR_KEY = "1";
const WELL_DATA_EDITOR_KEY = "2";
const TYPE_WELL_EDITOR_KEY = "3";
const TYPE_WELL_SELECTOR_KEY = "4";
const WELL_LIST_KEY = "5";
const CHANGE_RECORDS_KEY = "6";
const FORECAST_ARRAY_KEY = "7";

export function TypeWellEditorWidget() {
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const hasUnsavedChanges = useForecastStore(selectHasUnsavedChanges);
  const expandedTypeWellKeys = useForecastStore(selectExpandedTypeWellKeys);
  const expandedForecastKeys = useForecastStore(selectExpandedForecastKeys);

  const setSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
  const setHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
  const setExpandedTypeWellKeys = useForecastStore((s) => s.setExpandedTypeWellKeys);
  const setExpandedForecastKeys = useForecastStore((s) => s.setExpandedForecastKeys);

  const { declineType: declineTypeSetting } = useUserSettings();
  const declineType = declineTypeSetting?.decline ?? DEFAULT_DECLINE_TYPE_SETTING;

  const undoRedoManager = useUndoRedo();
  const arpsUndoManager = useArpsUndoManager();

  const { onSaveTypeWell, onCloneTypeWell, saveStatus } =
    useTypeWellSaver(undoRedoManager);

  const type = TYPE_WELLS;
  const selectedTypeWellRef = useRef(selectedTypeWell);
  const { resetTypeWell } = useTypeWellResetter(type, undoRedoManager);
  const { onTypeWellValueChange } = useTypeWellUpdater(undoRedoManager);
  const { onUpdateValNavTypeWell } = useValNavTypeWellUpdater(type);
  const expandedKeys = type === TYPE_WELLS ? expandedTypeWellKeys : expandedForecastKeys;

  const filteredForecastFolders = useSelector(
    (state: RootState) =>
      state.arps[
        type === TYPE_WELLS
          ? FilteredFolders.FilteredTypeWellFolders
          : FilteredFolders.FilteredForecastFolders
      ]
  );

  const handleBeforeUnload = useCallback((event) => {
    const message = "Are you sure you want to leave? Data may be lost.";
    event.preventDefault();
    event.returnValue = message;
    return message;
  }, []);

  useEffect(() => {
    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [hasUnsavedChanges, handleBeforeUnload]);

  useEffect(() => {
    if (saveStatus.error && !saveStatus.loading && saveStatus.called) {
      // Error is already logged to Sentry in useTypeWellSaver hook
      // Just display user-friendly error message to user
      toast.error(SAVE_TYPE_WELL_ERROR_MESSAGE);
    } else if (!saveStatus.loading && saveStatus.called && !saveStatus.error) {
      toast.success(`Successfully saved ${selectedTypeWellRef.current?.title}`);
    }
  }, [saveStatus.error, saveStatus.called, saveStatus.loading]);

  const [activeMenu, setActiveMenu] = useState(TYPE_WELL_SELECTOR_KEY);
  const [activeTab, setActiveTab] = useState(ARPS_EDITOR_KEY);

  useEffect(() => {
    selectedTypeWellRef.current = selectedTypeWell;
  }, [selectedTypeWell]);

  useEffect(() => {
    if (selectedTypeWell?.editLocation === "Activity" && selectedTypeWell) {
      setActiveMenu(TYPE_WELL_EDITOR_KEY);
    } else {
      setActiveMenu(TYPE_WELL_SELECTOR_KEY);
    }
  }, [selectedTypeWell]);

  const isEditorTabActive = activeMenu === TYPE_WELL_EDITOR_KEY;

  const setTabToEditor = () => {
    setActiveMenu(TYPE_WELL_EDITOR_KEY);
  };

  const toggleView = async () => {
    if (activeMenu === TYPE_WELL_SELECTOR_KEY) {
      setActiveMenu(TYPE_WELL_EDITOR_KEY);
    } else {
      handleSwitchToSelector();
    }
  };

  const handleSwitchToSelector = () => {
    const selectedTypeWellFolderIndex = filteredForecastFolders.findIndex(
      (folder) => folder.folderId === selectedTypeWell?.folderId
    );

    // When the user switches to the selector, always expand the folder that the selected type well is in.
    // Note: This is an edge case because when a type well is edited, it should be expanded,
    // however you can still close the folder from the projects tab.
    const selectedTypeWellFolder = filteredForecastFolders[selectedTypeWellFolderIndex];
    if (selectedTypeWellFolder?.id && !expandedKeys.includes(selectedTypeWellFolder.id)) {
      const setExpandedKeys =
        type === TYPE_WELLS ? setExpandedTypeWellKeys : setExpandedForecastKeys;
      setExpandedKeys([...expandedKeys, selectedTypeWellFolder.id]);
    }
    setSelectedTypeWell(null);
    setHasUnsavedChanges(false);
    setActiveMenu(TYPE_WELL_SELECTOR_KEY);
  };

  const TypeWellEditor = () => {
    return (
      <TypeWellEditorHeader
        handleSave={onSaveTypeWell}
        handleClone={onCloneTypeWell}
        isEditTypeWellPanelOpen={isEditorTabActive ? true : null}
        isWidget={true}
        saveStatus={saveStatus}
        type={type}
        undoRedoManager={undoRedoManager}
        fieldLevelUndoManager={arpsUndoManager}
        hideButtons={!isEditorTabActive}
      />
    );
  };

  return (
    <TypeWellWidgetContainer data-testid="widget-typewell-editor-widget">
      <Header>
        {activeMenu === TYPE_WELL_EDITOR_KEY &&
          (hasUnsavedChanges ? (
            <Popconfirm
              placement="bottom"
              title="You have unsaved changes. Proceed without saving?"
              okText="Confirm"
              cancelText="Cancel"
              onConfirm={toggleView}>
              <StyledButton data-testid="switch-to-selector-button">
                <Icon path={mdiMenu} size={1.2} />
              </StyledButton>
            </Popconfirm>
          ) : (
            <Tooltip title={"Switch to Selector"}>
              <StyledButton data-testid="switch-to-selector-button" onClick={toggleView}>
                <Icon path={mdiMenu} size={1.2} />
              </StyledButton>
            </Tooltip>
          ))}
        {activeMenu === TYPE_WELL_EDITOR_KEY && TypeWellEditor()}
      </Header>

      {activeMenu === TYPE_WELL_SELECTOR_KEY ? (
        <ForecastProvider>
          <ProjectForecasts
            type={TYPE_WELLS}
            location="Widget"
            widgetProps={{ setWidgetActiveTabKey: setTabToEditor }}
          />
        </ForecastProvider>
      ) : (
        <Tabs
          defaultActiveKey={ARPS_EDITOR_KEY}
          activeKey={activeTab}
          onTabClick={setActiveTab}
          items={[
            {
              key: ARPS_EDITOR_KEY,
              label: <div className="customArpsTabWidth">Arps</div>,
              children: (
                <TypeWellInputWrapper>
                  <TypeWellInputTable
                    wellKey={selectedTypeWell?.key}
                    arps={selectedTypeWell}
                    handleReload={onUpdateValNavTypeWell}
                    handleReset={resetTypeWell}
                    handleSave={onSaveTypeWell}
                    declineType={declineType}
                    onChange={onTypeWellValueChange}
                    saveStatus={saveStatus}
                    selectedTypeWellTitle={selectedTypeWell?.title}
                    selectedTypeWellRescat={selectedTypeWell?.reserveCategory}
                    kind={ArpsDesignerKind.TypeWell}
                    fieldLevelUndoManager={arpsUndoManager}
                  />
                </TypeWellInputWrapper>
              )
            },
            {
              key: FORECAST_ARRAY_KEY,
              label: "Forecast Array",
              children:
                activeTab === FORECAST_ARRAY_KEY ? (
                  <ForecastArrayContent selectedTw={selectedTypeWell} isActive={true} />
                ) : null
            },
            {
              key: WELL_DATA_EDITOR_KEY,
              label: "Well Data",
              children: (
                <EditTypeWellData
                  selectedTypeWell={selectedTypeWell}
                  onChange={(wellData) => {
                    const userArps = {
                      ...selectedTypeWell,
                      wellData
                    };
                    onTypeWellValueChange(userArps);
                  }}
                />
              )
            },
            {
              key: WELL_LIST_KEY,
              label: "Well List",
              children: (
                <TypeWellEditorWellListWrapper>
                  <TypeWellEditorWellList arpsItem={selectedTypeWell} />
                </TypeWellEditorWellListWrapper>
              )
            },
            {
              key: CHANGE_RECORDS_KEY,
              label: "Change Records",
              children: (
                <ChangeRecordsTabWrapper>
                  <TypeWellCommentField selectedTypeWell={selectedTypeWell} />
                  <ChangeRecordsScrollArea>
                    <TypeWellEditorChangeRecords forecastId={selectedTypeWell?.id} />
                  </ChangeRecordsScrollArea>
                </ChangeRecordsTabWrapper>
              )
            }
          ]}
        />
      )}
    </TypeWellWidgetContainer>
  );
}

const TypeWellEditorWellListWrapper = styled.div`
  position: relative;
  height: 100%;
`;

// The Change Records tab pane is height-bounded (.ant-tabs-tabpane { height: 100% })
// and clips overflow (.ant-tabs-content { overflow: hidden }). The comment field must
// stay fixed at the top while the records list scrolls in the remaining space, so lay
// the tab out as a flex column with a bounded, scrollable body. Without this the list's
// own height:100% is pushed below the pane by the comment field and its scrollbar is
// clipped off-screen.
const ChangeRecordsTabWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ChangeRecordsScrollArea = styled.div`
  flex: 1;
  min-height: 0;
`;

const StyledButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  color: ${(props) => (props.active ? "var(--color-primary)" : "#a2aaad")};
  background-color: transparent;
  border: none;
  font-weight: 500;
  padding: 0;
  margin: 0;
  transition: color 0.15s ease-out;
  overflow: hidden;
  pointer-events: all;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    pointer-events: none;
  }
`;

const Header = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  width: 100%;
  max-width: 100%;
  grid-template-columns: 36px 1fr;
  height: 48px;
  margin: 0; // Reset margin
  padding: 0; // Reset padding
`;

const TypeWellWidgetContainer = styled.div`
  width: 100%;
  height: 100%;
  grid-template-rows: auto 1fr;
  display: grid;
  overflow-x: auto;

  //Temp Fix to help with arps tab visiblilty
  .customArpsTabWidth {
    padding: 0 15px;
  }

  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom: 1px solid #d9e1e2;
  }

  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap {
    &:before {
      box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.2);
    }
    &:after {
      box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.2);
    }
  }

  // .ant-tabs-nav-list {
  //   width: 100%;
  // }

  .ant-tabs-tab {
    padding-top: 5px;

    .ant-tabs-tab-active {
      caret-color: transparent;
    }
  }

  .ant-tabs-nav-list .ant-tabs-tab .ant-tabs-tab-btn {
    font-size: 1.4rem;
    color: #a2aaad;
    letter-spacing: 0.5px;
    padding: 0 15px 0;
  }

  .ant-tabs-nav-list .ant-tabs-tab .ant-tabs-tab-btn:hover {
    color: var(--color-text);
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--color-text);
    font-weight: 700;
  }

  .ant-tabs-top > .ant-tabs-nav .ant-tabs-ink-bar {
    height: 3px;
    left: ${(props) => (props.activeKey - 1) * 157}px !important;
  }

  .ant-tabs > .ant-tabs-nav,
  .ant-tabs > div > .ant-tabs-na {
    margin: 0;
  }

  .ant-tabs-ink-bar {
    background: var(--color-primary);
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0;
  }

  .ant-tabs {
    min-height: 0;
    overflow-x: auto;
  }

  .ant-tabs .ant-tabs-content {
    height: 100%;

    .ant-tabs-tabpane {
      height: 100%;
    }
  }

  .ant-tabs-content {
    overflow: hidden;
    overflow-x: auto;
  }

  .ant-tree {
    background: transparent;
  }

  .ant-tree-switcher {
    background: transparent;
  }
`;

const TypeWellInputWrapper = styled.div`
  height: 100%;
`;
